import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { hasAdminScope, roleHome } from '@/lib/auth';

function FullScreenLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-white">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto mb-4 rounded-2xl bg-[#062B1A] flex items-center justify-center ring-1 ring-[#D4AF37]/30">
          <img src="/assets/mcc-logo-outlined.png" alt="MCC" className="w-8 h-8 object-contain animate-pulse" style={{ filter: 'brightness(0) invert(1)' }} />
        </div>
        <p
          className="text-[#062B1A] font-medium"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          Loading workspace…
        </p>
      </div>
    </div>
  );
}

// How long to keep retrying before giving up and sending to login
const PROFILE_RETRY_MS = 20000;

/**
 * Wraps a route subtree and:
 *  - shows a loader while auth is hydrating
 *  - bounces unauthenticated visitors to /login (preserving the original target)
 *  - if the user is logged in but the profile is slow, waits up to
 *    PROFILE_RETRY_MS before falling back to the login recovery screen
 *  - if `allow` is set, denies users whose role isn't in the list and routes them
 *    to their own dashboard
 */
export default function ProtectedRoute({ allow, adminScope, children }) {
  const { user, profile, loading, refreshProfile } = useAuth();
  const location = useLocation();
  const [profileTimedOut, setProfileTimedOut] = useState(false);

  // When loading finishes but profile is still missing, give the backend extra
  // time to warm up (retry once) before redirecting to login.
  useEffect(() => {
    if (!loading && user && !profile && !profileTimedOut) {
      const timer = window.setTimeout(() => {
        refreshProfile().then((p) => {
          if (!p) setProfileTimedOut(true);
        });
      }, PROFILE_RETRY_MS);
      return () => window.clearTimeout(timer);
    }
  }, [loading, user, profile, profileTimedOut, refreshProfile]);

  if (loading) return <FullScreenLoader />;

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Profile still loading (warm-up retry in progress) — keep showing loader.
  if (!profile && !profileTimedOut) return <FullScreenLoader />;

  // Gave up waiting for profile — send to login recovery screen.
  if (!profile && profileTimedOut) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname, profileIssue: true }}
      />
    );
  }

  if (allow && !allow.includes(profile.role)) {
    return <Navigate to={roleHome(profile) || '/login'} replace />;
  }

  if (profile.role === 'admin' && adminScope && !hasAdminScope(profile, adminScope)) {
    return <Navigate to={roleHome(profile)} replace />;
  }

  return children;
}
